import { useMutation } from '@tanstack/react-query';
import type { UseMutateFunction } from '@tanstack/react-query';
import { API_INTERACT_URL, axiosInt } from '../utils';
import { useMessage } from './useMessage';

interface IUploadFileParams {
  file: File;
}

export interface IUploadFileResponse {
  id: string;
  disk: string;
  bucket: string;
  path: string;
  name: string;
  size: number;
  extension: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  createdBy: {
    type: string;
    id: string;
    display: string;
    organizationId: string;
  };
}

export const UPLOAD_FILE_KEY = 'uploadFile';
export const uploadFileApi = async (
  file: File
): Promise<IUploadFileResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axiosInt.post(
    `${API_INTERACT_URL}/files/upload`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  return response.data;
};

export const useUploadFile = () => {
  const message = useMessage();

  const {
    mutate,
    isPending: isUploadingFile,
    error: errorUploadingFile,
    mutateAsync,
  } = useMutation<IUploadFileResponse, Error, IUploadFileParams>({
    mutationKey: [UPLOAD_FILE_KEY],
    mutationFn: ({ file }) => uploadFileApi(file),
    onSuccess: (_data, variables) => {
      message.success(`${variables.file.name} uploaded successfully!`);
    },
    onError: (error: unknown, variables) => {
      const err = error as {
        response?: { data?: { detail?: string; title?: string } };
        message?: string;
      };
      const errorMessage =
        err?.response?.data?.detail ||
        err?.response?.data?.title ||
        err?.message ||
        `Failed to upload ${variables.file.name}`;
      message.error(errorMessage);
    },
  });

  const uploadFile: UseMutateFunction<
    IUploadFileResponse,
    Error,
    IUploadFileParams,
    unknown
  > = mutate;

  const uploadFileAsync = (variables: IUploadFileParams) =>
    mutateAsync(variables);

  return {
    uploadFile,
    isUploadingFile,
    errorUploadingFile,
    uploadFileAsync,
  };
};
